import React from 'react';
import { Button, Input, InputProps } from '@flixbus/honeycomb-react';
import {
  Icon,
  IconCalendarSolid,
  IconClose,
  IconPlus,
  IconArrowLeft,
  IconArrowRight,
} from 'app/icons';
import classnames from 'classnames';
import * as styles from './DateInput.scss';
import { a11yLabelsKeys } from 'app/providers/a11yLabels';
import { useTranslations } from 'app/hooks/useTranslations';
import { TextWithEllipsis } from 'app/components/ui/TextWithEllipsis/TextWithEllipsis';
import { VoButton } from 'app/components/ui/VoButton/VoButton';
import { KEY_NAMES } from 'app/providers/keyNames';
import { dateLabelSelector } from 'app/store/selectors';
import { isDateToday } from 'app/functions';
import { emitDateArrowClickEvent } from 'app/events/emitDateArrowClickEvent';
import { useAppSelector } from 'app/hooks/appHooks';

type DateInputProps = {
  date: Date;
  label: string;
  isDepartureFocused?: boolean;
  onCrossButtonClick?: () => void;
  isArrival?: boolean;
  isExpanded?: boolean;
  showTooltipArrow?: boolean;
  valid?: boolean;
  onClick: (e: React.SyntheticEvent) => void;
  returnDateVisible?: boolean;
  onDateChange: (date: Date) => void;
};

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      date,
      label,
      onCrossButtonClick,
      isArrival,
      isExpanded,
      showTooltipArrow,
      valid,
      isDepartureFocused,
      onClick,
      returnDateVisible,
      onDateChange,
    },
    ref,
  ) => {
    const {
      getDeleteArrivalAriaLabel,
      getDepartureAriaLabel,
      getFilledDepartureAriaLabel,
      getArrivalAriaLabel,
      getFilledArrivalAriaLabel,
      getDepartureInThePastError,
      getPrevDayButton,
      getNextDayButton,
    } = useTranslations({
      getDeleteArrivalAriaLabel: a11yLabelsKeys.dates.deleteArrival,
      getDepartureAriaLabel: a11yLabelsKeys.dates.departure,
      getFilledDepartureAriaLabel: a11yLabelsKeys.dates.departureFilled,
      getArrivalAriaLabel: a11yLabelsKeys.dates.arrival,
      getFilledArrivalAriaLabel: a11yLabelsKeys.dates.arrivalFilled,
      getDepartureInThePastError: 'search_mask.error.departure_date_in_the_past',
      getPrevDayButton: a11yLabelsKeys.dates.prevDay,
      getNextDayButton: a11yLabelsKeys.dates.nextDay,
    });
    const getDateLabel = useAppSelector(dateLabelSelector);

    const dateLabel = getDateLabel(date);
    const isToday = isDateToday(date);
    const getTomorrow = () => {
      const tomorrow = new Date(date);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    };

    const getYesterday = () => {
      const yesterday = new Date(date);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday;
    };

    const renderArrivalIcon = (): Partial<InputProps> =>
      !onCrossButtonClick
        ? { iconLeft: <Icon appearance="primary" InlineIcon={IconPlus} /> }
        : {
            iconRight: (
              <Button
                extraClasses={styles.crossButton}
                onClick={(e: React.SyntheticEvent) => {
                  e.stopPropagation();
                  onCrossButtonClick();
                }}
                tabIndex={isArrival && isDepartureFocused ? -1 : 0}
                aria-hidden={isArrival && isDepartureFocused ? true : false}
                aria-label={getDeleteArrivalAriaLabel()}
              >
                <Icon InlineIcon={IconClose} size={3} />
              </Button>
            ),
          };
    const ariaLabel = `${
      isArrival
        ? dateLabel
          ? getFilledArrivalAriaLabel()
          : getArrivalAriaLabel()
        : dateLabel
          ? getFilledDepartureAriaLabel()
          : getDepartureAriaLabel()
    } ${dateLabel}`;

    const isValid = !valid && !isArrival ? false : undefined;

    return (
      <div className={classnames(styles.inputWrapper, { [styles.tooltipArrow]: showTooltipArrow })}>
        <VoButton
          ariaLabel={ariaLabel}
          onClick={onClick}
          tabIndex={isArrival && isDepartureFocused ? -1 : 0}
          ariaHidden={isArrival && isDepartureFocused ? true : false}
          ariaControls={`dialog-date-${isArrival ? 'to' : 'from'}`}
          isExpanded={isExpanded}
        >
          {dateLabel}
        </VoButton>
        <Input
          readOnly={true}
          aria-hidden={true}
          tabIndex={-1}
          extraClasses={styles.input}
          id={`dateInput-${isArrival ? 'to' : 'from'}`}
          label={
            <span aria-hidden={true}>
              <TextWithEllipsis text={label || '...'} />
            </span>
          }
          aria-expanded={isExpanded}
          value={dateLabel}
          iconLeft={
            !isArrival ? (
              <Icon appearance="primary" aria-hidden InlineIcon={IconCalendarSolid} />
            ) : undefined
          }
          valid={isValid}
          infoError={getDepartureInThePastError()}
          onClick={onClick}
          {...(isArrival ? renderArrivalIcon() : {})}
          innerRef={ref}
          onKeyDown={(e) => {
            if (e.key === KEY_NAMES.ENTER || e.key === KEY_NAMES.SPACE) {
              e.preventDefault();
              return onClick(e);
            }
          }}
          controllers={
            !returnDateVisible && !isArrival
              ? [
                  <Button
                    disabled={isToday}
                    key="button-prev"
                    onClick={() => {
                      emitDateArrowClickEvent('previous');
                      onDateChange(getYesterday());
                    }}
                    aria-label={getPrevDayButton({ date: getDateLabel(getYesterday()) })}
                  >
                    <Icon InlineIcon={IconArrowLeft} aria-hidden={true} />
                  </Button>,
                  <Button
                    key="button-next"
                    onClick={() => {
                      emitDateArrowClickEvent('next');
                      onDateChange(getTomorrow());
                    }}
                    aria-label={getNextDayButton({ date: getDateLabel(getTomorrow()) })}
                  >
                    <Icon InlineIcon={IconArrowRight} aria-hidden={true} />
                  </Button>,
                ]
              : undefined
          }
        />
      </div>
    );
  },
);

DateInput.displayName = 'DateInput';

export { DateInput };
